import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Check, Building2, Users, Shield, Headphones, Loader2 } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { MarketingNav } from "@/components/Marketing/MarketingNav";
import { MarketingFooter } from "@/components/Marketing/MarketingFooter";

const Enterprise = () => {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    teamSize: "",
    email: "",
    phone: "",
    message: ""
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { error } = await (supabase as any)
        .from('enterprise_leads')
        .insert({
          company_name: formData.companyName,
          team_size:    formData.teamSize || null,
          email:        formData.email,
          phone:        formData.phone || null,
          message:      formData.message || null,
        });
      if (error) throw error;
      toast({
        title: "Request received",
        description: "Our enterprise team will contact you within 24 hours.",
      });
      setFormData({ companyName: "", teamSize: "", email: "", phone: "", message: "" });
    } catch (err: any) {
      toast({
        title: "Submission failed",
        description: err.message ?? "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <MarketingNav />

      {/* Hero */}
      <section className="border-b bg-gradient-to-br from-primary/5 to-background">
        <div className="container mx-auto px-4 py-20 md:py-32">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-6xl font-bold">
              Enterprise-Grade Social Media Management
            </h1>
            <p className="text-xl text-muted-foreground text-justify">
              Built for organizations that need advanced security, dedicated support,
              and unlimited scale
            </p>
            <Button size="lg">Schedule a Demo</Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 py-20">
        <div className="grid md:grid-cols-2 gap-12 mb-20">
          <div>
            <h2 className="text-3xl font-bold mb-12">Enterprise Features</h2>
            <div className="space-y-8">
              <FeatureItem 
                icon={Building2}
                title="Multi-Team Workspaces"
                description="Organize teams, set permissions, and manage multiple brands from one account"
              />
              <FeatureItem 
                icon={Users}
                title="Advanced Collaboration"
                description="Role-based access, approval workflows, and team activity logs"
              />
              <FeatureItem 
                icon={Shield}
                title="Enterprise Security"
                description="SSO, SOC 2 compliance, data residency options, and dedicated infrastructure"
              />
              <FeatureItem 
                icon={Headphones}
                title="Dedicated Support"
                description="24/7 priority support, dedicated account manager, and custom SLA"
              />
            </div>
          </div>

          {/* Contact Form */}
          <Card className="p-8 h-fit">
            <h3 className="text-2xl font-bold mb-6">Get Started with Enterprise</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="companyName">Company Name</Label>
                <Input 
                  id="companyName"
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="teamSize">Team Size</Label>
                <Input 
                  id="teamSize"
                  placeholder="e.g., 10-50"
                  value={formData.teamSize}
                  onChange={(e) => setFormData({ ...formData, teamSize: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Work Email</Label>
                <Input 
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input 
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="message">Tell us about your needs</Label>
                <Textarea 
                  id="message"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                />
              </div>
              <Button type="submit" size="lg" className="w-full" disabled={submitting}>
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Request Demo
              </Button>
            </form>
          </Card>
        </div>

        {/* Benefits List */}
        <div className="border-t pt-20">
          <h3 className="text-2xl font-bold mb-8 text-center">What's Included</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <BenefitCard benefit="Unlimited users and social accounts" />
            <BenefitCard benefit="Custom AI model training" />
            <BenefitCard benefit="White-label options" />
            <BenefitCard benefit="Advanced analytics & reporting" />
            <BenefitCard benefit="API access & webhooks" />
            <BenefitCard benefit="Dedicated infrastructure" />
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

const FeatureItem = ({ icon: Icon, title, description }: {
  icon: any;
  title: string;
  description: string;
}) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0">
      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
        <Icon className="h-6 w-6 text-primary" />
      </div>
    </div>
    <div>
      <h3 className="font-bold mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground text-justify">{description}</p>
    </div>
  </div>
);

const BenefitCard = ({ benefit }: { benefit: string }) => (
  <Card className="p-4 flex items-center gap-3">
    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
      <Check className="h-4 w-4 text-primary" />
    </div>
    <span className="text-sm">{benefit}</span>
  </Card>
);

export default Enterprise;
